import axios from 'axios';

// No hardcoded default API URL - will be set dynamically from Firebase
let DEFAULT_API_URL: string | null = null;

// Function to set the default API URL dynamically
export const setDefaultApiUrl = (url: string) => {
  DEFAULT_API_URL = url;
  console.log('Default API URL set to:', url);
};

// Function to create an API instance for a specific hotel
export const createApiForHotel = (serverUrl?: string, serverPort?: number | null) => {
  let baseURL: string;
  
  if (serverUrl) {
    // If we have a server URL, use it
    if (serverUrl.startsWith('http://') || serverUrl.startsWith('https://')) {
      baseURL = `${serverUrl}${serverPort ? `:${serverPort}` : ''}/api`;
    } else {
      baseURL = `http://${serverUrl}${serverPort ? `:${serverPort}` : ''}/api`;
    }
  } else if (DEFAULT_API_URL) {
    baseURL = DEFAULT_API_URL;
  } else {
    throw new Error('No API URL available. Please configure a hotel with serverUrl in Firebase.');
  }
  
  return axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json'
    },
    timeout: 15000
  });
};

// Create default axios instance - will be created after default URL is set
let api: ReturnType<typeof createApiForHotel> | null = null;

// Function to initialize the default API instance
export const initializeDefaultApi = () => {
  if (!api) {
    api = createApiForHotel();
    setupInterceptors(api);
  }
  return api;
};

// Function to setup interceptors on an API instance
const setupInterceptors = (apiInstance: any): any => {
  // Add request interceptor to add auth token
  apiInstance.interceptors.request.use(
    (config: any) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error: any) => Promise.reject(error)
  );

  // Add response interceptor to handle token expiration and format errors
  apiInstance.interceptors.response.use(
    (response: any) => response,
    (error: any) => {
      // Handle authentication errors
      if (error.response?.status === 401) {
        console.error("Unauthorized (401) error from API:", error.config?.url);
        console.log("Clearing auth state due to 401 error");
        
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('selectedHotel');
        
        // Don't redirect from interceptor if we're already on the login page
        const isLoginPage = window.location.pathname.includes('/login');
        if (!isLoginPage) {
          console.log("Redirecting to login page due to 401 error");
          window.location.href = '/login';
        }
        
        return Promise.reject(error);
      }
      
      // Handle Service Unavailable (503) errors with a better format
      if (error.response?.status === 503) {
        console.warn('Service Unavailable (503) error:', error.config?.url);
        
        // Return a fake successful response with success: false
        return Promise.resolve({
          data: {
            success: false,
            message: "Failed to start playback. Audio device may be unavailable."
          },
          status: 200,
          statusText: 'OK',
          headers: error.response.headers,
          config: error.config,
        });
      }
      
      // Standardize error format for other errors
      if (error.response?.data) {
        // If error is a complex object (like ASP.NET Problem Details)
        if (typeof error.response.data === 'object' && error.response.data.title) {
          const aspNetError = error.response.data;
          // Create a more user-friendly error message
          error.message = aspNetError.title || 'An error occurred';
          
          // Add details if available
          if (aspNetError.detail) {
            error.details = aspNetError.detail;
          }
          
          // Add validation errors if available
          if (aspNetError.errors) {
            error.validationErrors = aspNetError.errors;
          }
        }
      }
      
      return Promise.reject(error);
    }
  );

  return apiInstance;
};

// Current API instance that will be used for all calls
let currentApi: ReturnType<typeof createApiForHotel> | null = null;

// Function to update the current API with a hotel-specific one
export const updateApiForHotel = (serverUrl?: string, serverPort?: number | null) => {
  const newApi = createApiForHotel(serverUrl, serverPort);
  setupInterceptors(newApi);
  currentApi = newApi;
  return currentApi;
};

// Store selected hotel in localStorage to remember between page loads
export const setSelectedHotel = (hotel: any) => {
  if (hotel) {
    localStorage.setItem('selectedHotel', JSON.stringify(hotel));
    updateApiForHotel(hotel.serverUrl, hotel.serverPort);
  } else {
    localStorage.removeItem('selectedHotel');
    currentApi = api; // Reset to default API
  }
};

// Get selected hotel from localStorage
export const getSelectedHotel = () => {
  const hotelJson = localStorage.getItem('selectedHotel');
  if (hotelJson) {
    try {
      return JSON.parse(hotelJson);
    } catch (e) {
      localStorage.removeItem('selectedHotel');
      return null;
    }
  }
  return null;
};

// Function to get the current API instance (lazy initialization)
export const getCurrentApi = (): ReturnType<typeof createApiForHotel> => {
  if (!currentApi) {
    currentApi = initializeDefaultApi();
  }
  return currentApi!;
};

// Initialize API with stored hotel if available
const storedHotel = getSelectedHotel();
if (storedHotel) {
  updateApiForHotel(storedHotel.serverUrl, storedHotel.serverPort);
} else {
  // Will be initialized when first API call is made
}

// Always export the current API instance getter function
export default getCurrentApi;